import {
  AddLink as AddLinkIcon,
  LockReset as LockResetIcon,
} from '@mui/icons-material';
import { GridColDef } from '@mui/x-data-grid-pro';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router';

import Logger from 'browser-logging/src/browser/Logger';

import { getRolesAllowedToEdit } from 'common/src/authorization/editAccess';
import { ArrayOperators } from 'common/src/query-params/operators';
import { NESTED_ROUTES } from 'common/src/routes/BaseRoutes';
import { DevFeatures } from 'common/src/types/enums/ProductFeatures.enum';
import { IPage, IUser } from 'common/src/types/interfaces';

import {
  forgotPasswordService,
  generateWelcomeOrResetPasswordUrl,
} from 'portal/src/api/authService';
import {
  getUsersService,
  updateUserService,
} from 'portal/src/api/UsersService';
import useAuth from 'portal/src/auth/useAuth';
import type { DeactivateConfig } from 'portal/src/components/common/CustomToolbar';
import { ALL_ORGANIZATIONS_ID, NEW_ID } from 'portal/src/constants/ReservedIDs';
import { MESSAGES } from 'portal/src/constants/snackbarMessages';
import { useEntityDialog } from 'portal/src/hooks/useEntityDialog';
import { defaultSysAdminStates } from 'portal/src/providers/DefaultPageStates';

import useSnackbarHandler from '../../utils/Snackbar/useSnackbarHandler';
import CopyTextDialog from '../common/CopyTextDialog';
import DataTable from '../common/DataTable';
import addBooleanColumnProps from '../common/tables/addBooleanColumnProps';

const Users = ({
  organizationId,
  routeFragment,
}: {
  organizationId?: string;
  routeFragment?: string;
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const snackbarHandler = useSnackbarHandler();
  const { featureChecker, hasAdminAccess, getUser } = useAuth();
  const loggedInUserId = getUser().id;
  const [showDeactivatedUsers, setShowDeactivatedUsers] = useState(false);

  const generatedUrlDialog = useEntityDialog<IUser>();

  const showUsersForAllOrgs = organizationId === ALL_ORGANIZATIONS_ID;

  const {
    data: users,
    isFetching,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      let additionalQueryParams = {};
      if (showUsersForAllOrgs) {
        additionalQueryParams = { allOrganizations: true };
      } else if (organizationId) {
        additionalQueryParams = { organizationId };
      }
      return await getUsersService({
        filters: [
          {
            field: 'userType',
            operator: ArrayOperators.IN,
            value: getRolesAllowedToEdit(getUser().userType),
          },
        ],
        ...additionalQueryParams,
      });
    },
    initialData: { data: [] } as IPage<IUser>,
  });

  const {
    data: welcomeOrResetUrl,
    isPending: isGeneratingUrlPending,
    isError: isGeneratingUrlError,
  } = useQuery({
    queryKey: ['generateUrl', generatedUrlDialog.data.email],
    queryFn: async () => {
      const response = await generateWelcomeOrResetPasswordUrl({
        email: generatedUrlDialog.data.email,
      });
      const { url } = await response.json();
      return url;
    },
    initialData: '',
    enabled: !!generatedUrlDialog.data.email,
  });

  const handleNavigate = (id: string) => {
    navigate(routeFragment ? `${routeFragment}/${id}` : id);
  };

  const handleRowDoubleClick = ({ row: { id } }: { row: { id: string } }) => {
    handleNavigate(id);
  };

  const handleResetPassword = async (email: string) => {
    try {
      await forgotPasswordService(
        { email },
        featureChecker.devFeatureEnabled(DevFeatures.WorkOsLogin),
      );
      snackbarHandler.success(MESSAGES.PASSWORD.RESET_EMAIL_SENT);
    } catch (err) {
      Logger.captureError(err as Error);
      snackbarHandler.error(MESSAGES.GENERIC.ERROR);
    }
  };

  const resetGeneratedUrlDialog = () => {
    generatedUrlDialog.closeDialog();
    queryClient.removeQueries({ queryKey: ['generateUrl'] });
  };

  const deactivateConfig: DeactivateConfig = {
    isChecked: showDeactivatedUsers,
    onToggleCheckbox: setShowDeactivatedUsers,
  };

  const gridColumns: GridColDef[] = [
    { field: 'name', headerName: 'Name', flex: 1, minWidth: 200 },
    ...(showUsersForAllOrgs
      ? [
          {
            field: 'organization',
            headerName: 'Organization',
            flex: 1,
            minWidth: 150,
            valueGetter: (value) => value?.name ?? '',
          },
        ]
      : []),
    {
      field: 'userType',
      headerName: 'User Role',
      flex: 1,
      minWidth: 100,
    },
    { field: 'email', headerName: 'Email', flex: 1, minWidth: 150 },
    { field: 'title', headerName: 'Title', flex: 0.5 },
    {
      field: 'isActive',
      headerName: 'Deactivated',
      ...addBooleanColumnProps({
        displayLogic: { valueForYes: false, yesIsGreen: false },
      }),
    },
  ];

  return (
    <>
      <DataTable
        actionsConfig={{
          extraActions: hasAdminAccess()
            ? (row: IUser) => [
                {
                  disabled: !row.isActive,
                  icon: <LockResetIcon />,
                  label: 'Send Reset Password Email',
                  onClick: () => handleResetPassword(row.email),
                },
                {
                  disabled: !row.isActive,
                  icon: <AddLinkIcon />,
                  label: 'Generate Reset Password Link',
                  onClick: () => generatedUrlDialog.openDialog(row),
                },
              ]
            : undefined,
          readActionConfig: {
            onClick: (id) => handleNavigate(id),
          },
          changeActiveConfig: {
            targetStr: 'user',
            isDisabledForRow: (row) => {
              if (!row.isActive) {
                return false;
              }

              return row.id === loggedInUserId;
            },
            onChangeActiveStatus: async (id, isActive) => {
              await updateUserService(id, { isActive: !isActive });
              await refetch();
            },
            snackbarMsgKey: 'USER',
          },
        }}
        columns={gridColumns}
        contextName={`${NESTED_ROUTES.USERS}${organizationId ? `_${organizationId}` : ''}`}
        dataFetchError={isError}
        initialState={{
          sorting: {
            sortModel: defaultSysAdminStates()[NESTED_ROUTES.USERS].sort,
          },
        }}
        loading={isFetching}
        onRowDoubleClick={handleRowDoubleClick}
        rows={
          showDeactivatedUsers
            ? users.data
            : users.data.filter(({ isActive }) => isActive)
        }
        toolbarSlotProps={{
          deactivateConfig,
          onNewButtonClick: () => handleNavigate(NEW_ID),
          showFilters: true,
        }}
      />
      <CopyTextDialog
        open={generatedUrlDialog.open}
        onClose={resetGeneratedUrlDialog}
        textToCopy={welcomeOrResetUrl}
        title={'Welcome/Reset Password Link'}
        loading={welcomeOrResetUrl === '' || isGeneratingUrlPending}
        disabledAction={isGeneratingUrlPending}
        errorMessage={
          isGeneratingUrlError
            ? 'Error generating welcome/reset password link. Please try again.'
            : ''
        }
      />
    </>
  );
};

export default Users;
